import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Typography,
  Button,
  Grid,
  TextField,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip
} from '@mui/material';
import { toast } from 'react-toastify';
import { Upload, ArrowLeft, Send } from 'lucide-react';
import api from '../../services/api';

function bytesLabel(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
  const value = n / Math.pow(1024, i);
  return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export default function AderDocumentoUpload() {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [clienteId, setClienteId] = useState('');
  const [praticaId, setPraticaId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState([]);

  const totalSize = useMemo(() => {
    return (files || []).reduce((acc, f) => acc + (f?.size || 0), 0);
  }, [files]);

  const handlePickFiles = (event) => {
    const list = Array.from(event.target.files || []);
    setFiles(list);
    setResults([]);
  };

  const handleUpload = async () => {
    if (!files.length) {
      toast.error('Seleziona almeno un file');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      files.forEach((f) => form.append('files', f));
      if (String(clienteId || '').trim()) form.append('cliente_id', String(clienteId).trim());
      if (String(praticaId || '').trim()) form.append('pratica_id', String(praticaId).trim());

      const res = await api.post('/ader/documenti/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const out = res.data?.results || [];
      setResults(out);
      toast.success('Upload completato');
    } catch (error) {
      toast.error('Upload fallito');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Card sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Button startIcon={<ArrowLeft size={18} />} onClick={() => navigate('/ader/coda-validazione')}>
              Coda validazione
            </Button>
            <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
              ADER · Carica documento
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Carica uno o più file (PDF) e avvia l’elaborazione automatica.
            </Typography>
          </Box>
        </Box>
      </Card>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              File
            </Typography>
            <Divider sx={{ my: 1.5 }} />

            <Button component="label" variant="outlined" startIcon={<Upload size={18} />}>
              Seleziona file
              <input hidden multiple type="file" accept="application/pdf" onChange={handlePickFiles} />
            </Button>

            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Selezionati: {files.length} · Totale: {bytesLabel(totalSize)}
              </Typography>
            </Box>

            {files.length > 0 ? (
              <List dense sx={{ mt: 1 }}>
                {files.map((f) => (
                  <ListItem key={`${f.name}-${f.size}`}>
                    <ListItemText primary={f.name} secondary={bytesLabel(f.size)} />
                  </ListItem>
                ))}
              </List>
            ) : null}
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Metadati (opzionali)
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="cliente_id"
                  value={clienteId}
                  onChange={(e) => setClienteId(e.target.value)}
                  placeholder="es. 123"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="pratica_id"
                  value={praticaId}
                  onChange={(e) => setPraticaId(e.target.value)}
                  placeholder="es. 456"
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  startIcon={<Send size={18} />}
                  onClick={handleUpload}
                  disabled={uploading || files.length === 0}
                >
                  {uploading ? 'Caricamento...' : 'Carica'}
                </Button>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Esito
            </Typography>
            <Divider sx={{ my: 1.5 }} />

            {results.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nessun esito disponibile
              </Typography>
            ) : (
              <List dense>
                {results.map((r, idx) => (
                  <ListItem key={`${r.documentoId || 'x'}-${idx}`}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Chip
                            size="small"
                            label={r.status || '-'}
                            color={r.status === 'uploaded' ? 'success' : r.status === 'duplicate' ? 'warning' : 'default'}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {r.nomeFile || '-'}
                          </Typography>
                        </Box>
                      }
                      secondary={`documentoId: ${r.documentoId || '-'}${r.jobId ? ` · jobId: ${r.jobId}` : ''}`}
                    />
                    <Button size="small" onClick={() => navigate('/ader/coda-validazione')}>
                      Vai alla coda
                    </Button>
                  </ListItem>
                ))}
              </List>
            )}
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

